import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps, Popup, useClosePopupOnNavigation } from '@gem/popups';
import { Button, ButtonTypes, PopupFooter, PopupHeader, Selector, TextField } from '@gem/ui-kit';

import { FORM_ID } from './CustomReminderPopup.constants';
import {
  ICustomReminderPopupParams,
  useCustomReminderPopupPresenter,
} from './CustomReminderPopup.presenter';
import styles from './CustomReminderPopup.styles.m.css';

export const CustomReminderPopup: FC<ICommonPopupsProps<ICustomReminderPopupParams>> = observer(
  (props) => {
    const { t } = useTranslation();
    useClosePopupOnNavigation(props.popupInstance);
    const { presenter, form } = useCustomReminderPopupPresenter(props);
    const {
      control,
      handleSubmit,
      formState: { isSubmitting, isValid, isValidating },
    } = form;
    const timeValue = form.watch('time');

    return (
      <Popup
        isOpened
        onClose={presenter.onClose}
        className={styles.popup}
      >
        <PopupHeader
          title={t('calendar_reminder_custom_title')}
          onClose={presenter.onClose}
        />
        <form
          id={FORM_ID}
          className={styles.form}
          onSubmit={handleSubmit(presenter.onSubmit)}
        >
          <div className={styles.container}>
            <Controller
              control={control}
              name="time"
              render={({ field }): ReactElement => (
                <TextField
                  {...field}
                  className={styles.countField}
                  type="number"
                  onKeyDown={presenter.handleKeyDown}
                  onPaste={presenter.handlePaste}
                />
              )}
            />
            <Controller
              control={control}
              name="unit"
              render={({ field }): ReactElement => (
                <Selector
                  {...field}
                  items={presenter.getUnitItems(timeValue)}
                  placement={'bottom-end'}
                  className={styles.unitField}
                  theme="standard"
                />
              )}
            />
            <Controller
              control={control}
              name="type"
              render={({ field }): ReactElement => (
                <Selector
                  {...field}
                  items={presenter.typeItems}
                  placement={'bottom-end'}
                  className={styles.typeField}
                  theme="standard"
                />
              )}
            />
          </div>
        </form>
        <PopupFooter>
          <Button
            type={ButtonTypes.Submit}
            form={FORM_ID}
            isLoading={isSubmitting}
            isDisabled={!isValid || isValidating}
          >
            {t('common_done')}
          </Button>
        </PopupFooter>
      </Popup>
    );
  }
);
